import { Schema } from 'prosemirror-model';
import { EditorState, Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

import { EMPTY_SELECTION } from '../helpers/utils';
import {
  EditorJSONState,
  EditorMode,
  IProsemirrorConfig,
  PluginsAPI,
  PluginsButtons,
  PluginWithPriority,
  RtPMPlugin,
} from './types';

type TypeOfArray<T> = T extends (infer U)[] ? U : T;
interface CreateEditorOptions {
  mode: EditorMode;
  initialState?: EditorJSONState | null;
}

const getNativeEditorPlugins = <T extends RtPMPlugin, S extends Schema>(
  plugins: T[],
  schema: S,
) => {
  const pluginsOrObjects = plugins.reduce(
    (acc, plugin) => [...acc, ...(plugin.getNativeEditorPlugins?.(schema) || [])],
    [],
  );
  const pluginObjects = pluginsOrObjects.map((pluginOrObject: PluginWithPriority) =>
    pluginOrObject.priority && pluginOrObject.plugin
      ? pluginOrObject
      : { priority: 50, plugin: <Plugin>(<unknown>pluginOrObject) },
  );
  const sortedObjects = [...pluginObjects].sort((a, b) =>
    a.priority < b.priority ? 1 : a.priority > b.priority ? -1 : 0,
  );
  return sortedObjects.map(({ plugin }) => plugin);
};

const getNativeViewPlugins = <T extends RtPMPlugin>(plugins: T[], mode: EditorMode) =>
  plugins.reduce((acc, plugin) => [...acc, ...(plugin.getNativeViewPlugins?.(mode) || [])], []);

const setContent = (editorView: EditorView, content: EditorJSONState) => {
  editorView.setProps({
    state: EditorState.fromJSON(editorView.state, {
      ...content,
      selection: content.selection || EMPTY_SELECTION,
    }),
  });
};

const createReadOnlyEditor = (
  editorElement: HTMLElement,
  schema: Schema,
  plugins: Plugin[],
  options: CreateEditorOptions,
) => {
  const editorView = new EditorView(editorElement, {
    state: EditorState.create({
      schema,
      plugins,
    }),
  });

  if (options.initialState) setContent(editorView, options.initialState);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { eventHandlers, domObserver } = <any>editorView;
  Object.entries(eventHandlers).forEach(([name, handler]: [string, () => void]) =>
    editorView.dom.removeEventListener(name, handler),
  );
  editorView.setProps({ editable: () => false });
  // domObserver.disconnectSelection();

  return editorView;
};

export const createEditor = <T extends RtPMPlugin, Config extends IProsemirrorConfig>(
  editorElement: HTMLElement,
  plugins: T[],
  config: Config,
  options: CreateEditorOptions,
) => {
  const schema = config.getSchema();
  const destroyPlugins = () => plugins.forEach(plugin => plugin.destroy?.());

  plugins.forEach(plugin => plugin.init?.(options.mode));
  const viewPlugins = getNativeViewPlugins(plugins, options.mode);

  if (EditorMode.View === options.mode) {
    const editorView = createReadOnlyEditor(editorElement, schema, viewPlugins, options);
    return {
      destroyPlugins,
      editorView,
    };
  }

  plugins.forEach(plugin => plugin.beforeEditorInit?.(schema));
  const editorPlugins = getNativeEditorPlugins(plugins, schema);

  const editorView = new EditorView(editorElement, {
    state: EditorState.create({
      schema,
      plugins: [...viewPlugins, ...editorPlugins],
    }),
    attributes: {
      class: 'editor',
    },
  });

  plugins.forEach(plugin => plugin.afterEditorInit?.(editorView));

  if (options.initialState) setContent(editorView, options.initialState);

  const publicApi: PluginsAPI<TypeOfArray<T>> = plugins.reduce((acc, plugin) => {
    Object.keys(plugin).forEach(key => {
      if (key in acc) {
        throw new Error(`Key conflict`);
      }
    });
    Object.assign(acc, plugin.api || {});
    return acc;
  }, <PluginsAPI<TypeOfArray<T>>>{});

  const buttons: PluginsButtons<TypeOfArray<T>> = plugins.reduce((acc, plugin) => {
    Object.keys(plugin).forEach(key => {
      if (key in acc) {
        throw new Error(`Key conflict`);
      }
    });
    Object.assign(acc, plugin.buttons || {});
    return acc;
  }, <PluginsButtons<TypeOfArray<T>>>{});

  return {
    editorView,
    schema,
    destroyPlugins,
    publicApi,
    buttons,
  };
};
